import get from 'lodash/get'
import isNil from 'lodash/isNil'
import throttle from 'lodash/throttle'
import isFunction from 'lodash/isFunction'

export const key = 'tooltip'

let originTooltipShowEventFn = null

function setEvent(options, chart) {
  const { onChange, onHide, onShow, ...newOptions } = options

  if (onChange) {
    newOptions.onChange = (obj) => {
      onChange(obj, chart)
    }
  }
  if (onHide) {
    newOptions.onHide = (obj) => {
      onHide(obj, chart)
    }
  }
  if (onShow) {
    newOptions.onShow = (obj) => {
      onShow(obj, chart)
    }
  }
  return newOptions
}

const showTooltip = throttle((event, chart) => {
  event.preventDefault && event.preventDefault()
  event.srcEvent && event.srcEvent.stopPropagation && event.srcEvent.stopPropagation()
  const el = chart.get('el')
  const { x, y } = (el && el.getBoundingClientRect()) || {}
  chart.showTooltip({ x: get(event, 'center.x', 0) - x, y: get(event, 'center.y', 0) - y })
}, 66.7)

const initInteraction = (chart) => {
  const gestureController = chart.get('gestureController')
  gestureController && gestureController.destroy()

  chart.pluginGesture({
    gesture: {
      press(data, event) {
        this.pressed = true
        showTooltip(event, chart)
      },
      touchend() {
        this.pressed = false
      },
      pan(data, event) {
        if (!this.pressed) return
        showTooltip(event, chart)
      }
    },
    options: {
      useCalculate: false,
    },
  })

  const { hammer } = chart.get('gestureController')
  const g = hammer.get('press')
  g.set({ time: 251 })
}

const initCustomInteraction = (chartInstance, getCurrentTooltip = null) => {
  const showTooltipAfterInteraction = () => {
    if (!isFunction(getCurrentTooltip)) return
    const tooltipConfig = getCurrentTooltip()
    if (!tooltipConfig || !Array.isArray(tooltipConfig.items) || tooltipConfig.items.length <= 0) return
    const data = tooltipConfig.items[0].origin
    const { values } = chartInstance.getXScale()
    if (!values.includes(data.name)) return
    chartInstance.showTooltip(chartInstance.getPosition(data))
  }

  chartInstance.interaction('pan', {
    onEnd: showTooltipAfterInteraction,
  })

  chartInstance.interaction('pinch', {
    maxScale: 5,
    minScale: 1,
    sensitivity: 1,
    onEnd: showTooltipAfterInteraction,
  })

  const tooltip = chartInstance.get('tooltipController')
  if (tooltip) {
    if (!originTooltipShowEventFn) originTooltipShowEventFn = tooltip.handleShowEvent
    tooltip.handleShowEvent = (...rest) => {
      const event = rest[0]
      const { originTicks } = (chartInstance._interactions && chartInstance._interactions.pinch) || {}
      if (originTicks) {
        const isZoom = Array.isArray(originTicks) && originTicks.length > chartInstance.getXScale().values.length
        // 放大时不需要移动事件
        if (isZoom) return
      }
      if (event && event.touches && event.touches.length > 1) return

      originTooltipShowEventFn.bind(tooltip)(...rest)
    }
  }

  const { pan, pinch } = chartInstance._interactions || {}

  chartInstance.set('_closeTooltip', false)
  if (pan && pan.hammer) {
    pan.hammer.off('press')
    pan.reset = function () {
    }
  }
  if (pinch && pinch.hammer) {
    pinch.hammer.off('press')
    pinch.reset = function () {
    }
  }
  chartInstance.pluginGesture({
    gesture: {
      tap(data, event) {
        const { originTicks } = (chartInstance._interactions && chartInstance._interactions.pinch) || {}
        if (originTicks) {
          const isZoom = Array.isArray(originTicks) && originTicks.length > chartInstance.getXScale().values.length
          // 放大时不需要移动事件
          if (!isZoom) return
        }
        showTooltip(event, chartInstance)
      },
    },
    options: {
      useCalculate: false,
    },
  })
}

const customTooltip = (chartInstance, options = {}, cb = null, detailMode = false, getCurrentTooltip = null) => {
  if (!cb) return options
  options.triggerOn = detailMode ? ['touchstart', 'touchmove'] : ['']
  options.triggerOff = detailMode ? '' : 'touchstart'
  options.custom = true
  options.onChange = (tooltip, chart) => {
    cb(tooltip, chart.getLegendItems().name)
  }
  options.onHide = () => {
    cb(null, null)
  }

  options.onShow = (tooltip, chart) => {
    const shapes = chart.get('geoms').map(d => d.get('container')).map(d => d.getChildren())
      .reduce((acc, items) => [...acc, ...items], [])
    if (!Array.isArray(shapes) || shapes.length <= 0) return

    const tooltipItems = Array.isArray(tooltip.items) ? tooltip.items : null

    if (!tooltipItems || tooltipItems.length <= 0) return
    const tooltipOrigin = tooltipItems.map(d => d.origin)

    const highlightShapes = shapes.filter(d => tooltipOrigin.includes(d.get('origin')._origin))
    if (!highlightShapes.length) return

    const otherShapes = shapes.filter(d => !highlightShapes.includes(d))
    highlightShapes.forEach(d => d.attr('opacity', 1))
    otherShapes.forEach((d) => {
      d.attr('opacity', 0.5)
    })

    chart.get('canvas').draw()
  }
  options.alwaysShow = true

  if (!detailMode) {
    initInteraction(chartInstance)
  } else {
    initCustomInteraction(chartInstance, getCurrentTooltip)
  }
  return options
}

export const process = (chart, config) => {
  const { customTooltipFn, getCurrentTooltip } = config || {}
  customTooltipFn && Reflect.deleteProperty(config, 'customTooltipFn')
  getCurrentTooltip && Reflect.deleteProperty(config, 'getCurrentTooltip')

  if (isNil(config) || config === false || config.show === false) {
    return chart.tooltip(false)
  }

  const { show, ...options } = config
  // beforeSetConfig = beforeSetConfig || customTooltip
  // // 可注册自定义事件等等
  // if (customTooltipFn) {
  //   options = beforeSetConfig(chart, options, customTooltipFn, detailMode, getCurrentTooltip)
  // }
  return chart.tooltip(setEvent(options, chart))
}
